import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { jwtDecode } from 'jwt-decode';
import { catchError, finalize, map, Observable, of, shareReplay } from 'rxjs';
import { ApiService } from '../constants/api-services.constant';
import { ApiUrlService } from '../services/api-url.service';
import { BrsGrant, BrsPermission, PermissionTokenPayload, PermissionTokenResponse, UserAccess } from './permission.model';

const PERMISSION_TOKEN_STORAGE_KEY = 'brs_permission_token';
const FEATURE_FLAG_PREFIX = 'featureFlags';
const NO_ACCESS: UserAccess = { permissions: [], grants: {} };

/**
 * Single source of truth for the permissions and grants of the current user,
 * read from the `X-Permission-Token`.
 *
 * Only used for UX (navigation, route guards). The backend still authorizes every request.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly http = inject(HttpClient);
  private readonly msalService = inject(MsalService);
  private readonly apiUrlService = inject(ApiUrlService);

  private readonly access = signal<UserAccess | null>(null);
  private readonly permissionKeys = computed(
    () => new Set((this.access()?.permissions ?? []).map((permission) => permission.toLowerCase()))
  );
  private pendingLoad$?: Observable<void>;

  readonly isLoaded = computed(() => this.access() !== null);

  token(): string | null {
    return sessionStorage.getItem(PERMISSION_TOKEN_STORAGE_KEY);
  }

  /** Stores the token and updates the permissions. Returns false if the token is rejected. */
  setToken(token: string): boolean {
    const payload = this.decode(token);
    if (!payload || !this.isValidForActiveSession(payload)) {
      return false;
    }

    sessionStorage.setItem(PERMISSION_TOKEN_STORAGE_KEY, token);
    this.access.set({
      ...payload.permissions,
      permissions: payload.permissions?.permissions ?? [],
      grants: payload.permissions?.grants ?? {},
    });
    return true;
  }

  hasPermission(permission: BrsPermission): boolean {
    return this.permissionKeys().has(`${FEATURE_FLAG_PREFIX}:${permission}`.toLowerCase());
  }

  /** A grant only authorizes something for a specific target, like the backend `UserAuthContext.HasGrant`. */
  hasGrant(grant: BrsGrant, targetId: string): boolean {
    if (!targetId) {
      return false;
    }
    const targets = this.access()?.grants[grant] ?? [];
    return targets.some((target) => target.toLowerCase() === targetId.toLowerCase());
  }

  /**
   * Makes sure the permissions are loaded: uses the stored token if it is still valid,
   * otherwise requests a new one. On failure the user is treated as having no permissions.
   */
  ensureLoaded(): Observable<void> {
    if (this.isLoaded()) {
      return of(undefined);
    }

    const storedToken = this.token();
    if (storedToken && this.setToken(storedToken)) {
      return of(undefined);
    }

    this.pendingLoad$ ??= this.http
      .post<PermissionTokenResponse>(this.apiUrlService.build(ApiService.UserPermissionManagement, 'auth', 'permissions'), null)
      .pipe(
        map((response) => this.setToken(response.token)),
        catchError(() => of(false)),
        map((accepted) => {
          if (!accepted && !this.isLoaded()) {
            this.access.set(NO_ACCESS);
          }
        }),
        finalize(() => (this.pendingLoad$ = undefined)),
        shareReplay(1)
      );

    return this.pendingLoad$;
  }

  clear(): void {
    sessionStorage.removeItem(PERMISSION_TOKEN_STORAGE_KEY);
    this.access.set(null);
    this.pendingLoad$ = undefined;
  }

  private decode(token: string): PermissionTokenPayload | null {
    try {
      return jwtDecode<PermissionTokenPayload>(token);
    } catch {
      return null;
    }
  }

  /** The token must belong to the current MSAL login and must not be expired. */
  private isValidForActiveSession(payload: PermissionTokenPayload): boolean {
    const activeNonce = this.msalService.instance.getActiveAccount()?.idTokenClaims?.nonce;
    const isExpired = payload.exp !== undefined && payload.exp * 1000 <= Date.now();
    return !!activeNonce && payload.jti_gea === activeNonce && !isExpired;
  }
}
